import { Toggle } from '@csp/csp-web-hooks';
import { AlertDialog, Body1, UnorderedList, UnorderedListItem } from '@csp/csp-web-ui';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarcodeInputContainer } from '../../../common/component/container/BarcodeInputContainer';
import { useBarcodeInputContext } from '../../../common/context/barcode-input-context/useBarcodeInputContext';
import { BarcodeType } from '../../../common/model/BarcodeType';
import { StaticContentService } from '../../../common/service/StaticContentService';
import { HbsSearchParamKey } from '../../hbs/type/HbsSearchParamKey';

type Props = {
  toggle: Toggle;
  enabledBarcodeTypes: BarcodeType[];
};

type BarcodeTypeSearchParamAndText = { searchParam: string; text: string };

const BARCODE_INPUT_BLOCKER_ID = 'BarcodeInputDialog';

export const BarcodeInputDialog: FC<Props> = ({ toggle, enabledBarcodeTypes }) => {
  const texts = StaticContentService.getGenericEntry('webHcpBarcodeInput');
  const [searchParams, setSearchParams] = useSearchParams();
  const { registerBlocker } = useBarcodeInputContext();

  const searchParamAndTextByBarcodeType = useMemo<Record<BarcodeType, BarcodeTypeSearchParamAndText>>(
    () => ({
      [BarcodeType.HBS_KIT_CODE]: {
        searchParam: HbsSearchParamKey.KIT_CODE,
        text: texts.dialog.body.enterBarcodeInfoKitBarcode.text,
      },
      [BarcodeType.HBS_SAMPLE_CODE]: {
        searchParam: HbsSearchParamKey.SAMPLE_CODE,
        text: texts.dialog.body.enterBarcodeInfoSampleBarcode.text,
      },
    }),
    [texts],
  );

  const enabledSearchParams = useMemo<string[]>(
    () => enabledBarcodeTypes.map(type => searchParamAndTextByBarcodeType[type].searchParam),
    [enabledBarcodeTypes, searchParamAndTextByBarcodeType],
  );

  const handleOnSubmit = (code: string, type: BarcodeType): void => {
    setSearchParams({ [searchParamAndTextByBarcodeType[type].searchParam]: code });
  };

  const blockExternalBarcodeInputs = useCallback(() => {
    registerBlocker({ isBlocking: true, id: BARCODE_INPUT_BLOCKER_ID });
  }, [registerBlocker]);

  const unblockExternalBarcodeInputs = useCallback(() => {
    registerBlocker({ isBlocking: false, id: BARCODE_INPUT_BLOCKER_ID });
  }, [registerBlocker]);

  useEffect(() => {
    const anyEnabledSearchParamExistsInUrl = enabledSearchParams.some(searchParam => searchParams.has(searchParam));
    if (anyEnabledSearchParamExistsInUrl) {
      toggle.setOff();
    }
  }, [searchParams, enabledBarcodeTypes, searchParamAndTextByBarcodeType, toggle, enabledSearchParams]);

  useEffect(() => {
    if (toggle.isOn) {
      blockExternalBarcodeInputs();
    } else {
      unblockExternalBarcodeInputs();
    }
  }, [toggle, blockExternalBarcodeInputs, unblockExternalBarcodeInputs]);

  return toggle.isOn ? (
    <AlertDialog
      open={toggle.isOn}
      onClose={toggle.setOff}
      header={texts.dialog.header.text}
      data-testid="barcode-input-dialog"
    >
      <Body1 mb={3}>{texts.dialog.body.scanningInstruction.text}</Body1>

      <UnorderedList>
        {enabledBarcodeTypes.map(type => (
          <UnorderedListItem key={type}>
            <Body1 fontWeight={600} mb={1}>
              {searchParamAndTextByBarcodeType[type].text}
            </Body1>
          </UnorderedListItem>
        ))}
      </UnorderedList>

      <Body1 mt={3} mb={2}>
        {texts.dialog.body.scanningNotAvailableInstruction.text}
      </Body1>

      <BarcodeInputContainer
        onSubmit={handleOnSubmit}
        texts={{
          textFieldPlaceholder: {
            withScan: texts.dialog.input.scanOrTypeBarcode.label,
            withoutScan: texts.dialog.input.enterManually.label,
          },
          addButtonLabel: texts.dialog.button.check.label,
        }}
        barcodeTypes={enabledBarcodeTypes}
        ignoreBlocking
      />
    </AlertDialog>
  ) : null;
};
